import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom';
import { searchPA, currencyFormat, patchByIdPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaCreditCard, 
  FaPlus, 
  FaSearch,
  FaUsers,
  FaCalendarAlt,
  FaToggleOn,
  FaToggleOff,
  FaTrash,
  FaPen,
  FaExclamationTriangle,
  FaTimes,
  FaLock
} from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Badge, Table, Modal } from 'react-bootstrap'
import './Plans.css'

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { users: plans, authenticatedUser, setUsers, setAlert } = useUserContext()

  const [keywords, setKeywords] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [planToDelete, setPlanToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const handleChangeKeywords = (e) => setKeywords(e.target.value)

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  const handleSearchPlan = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await searchPA({ path: 'plans', keywords, token: authenticatedUser.value })
      setUsers(response.data)
    } catch (error) {
      setAlert({
        message: 'Error al buscar planes',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatus = async (planId, status) => {
    try {
      let record = await patchByIdPA({ path: 'plans',
        id: planId,
        data: {
          active: !status
        }, 
        token: authenticatedUser.value })

        if(Object.prototype.hasOwnProperty.call(record, 'planId')) {
          setUsers(plans.map(plan => 
            plan.planId === planId 
              ? { ...plan, active: !status }
              : plan
          ))
          setAlert({
            message: `Plan ${status ? 'Desactivado' : 'Activado'} exitosamente`,
            msgType: 'success'
          })
        } else {
          setAlert({
            message: record.message,
            msgType: 'warning'
          })
        }
    } catch (error) {
      setAlert({
        message: 'Error al cambiar el estado del plan',
        msgType: 'warning'
      })
    }
  }

  const handleDelete = (plan) => {
    if (plan.hasMemberships) {
      // Mostrar modal informativo en lugar de alert
      setPlanToDelete(plan)
      setShowDeleteModal(true)
      return
    }
    
    setPlanToDelete(plan)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!planToDelete) return

    setIsDeleting(true)
    try {
      let record = await patchByIdPA({ path: 'plans',
        id: planToDelete.planId,
        data: {
          deleted: true,
        }, 
        token: authenticatedUser.value })

      if(Object.prototype.hasOwnProperty.call(record, 'planId')) {
        setUsers(plans.filter((plan) => plan.planId !== planToDelete.planId ))
        setAlert({
          message: `Plan eliminado exitosamente`,
          msgType: 'success'
        })
        setShowDeleteModal(false)
        setPlanToDelete(null)
      } else {
        setAlert({
          message: record.message,
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al eliminar el plan',
        msgType: 'warning'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setPlanToDelete(null)
    setIsDeleting(false)
  }

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await searchPA({ path: 'plans', keywords, token: authenticatedUser.value })
      if (response.error || !Array.isArray(response.data)) {
        setUsers([])
        setAlert({
          message: response.message || 'Error en la transacción. Si persiste el error, por favor contacte a soporte.',
          msgType: 'warning'
        })
      } else {
        setUsers(response.data)
      }
    } catch (error) {
      setAlert({
        message: 'Error al cargar planes',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [keywords])

  // Función para obtener el switch de estado (solo visual)
  const getStatusSwitch = (active, planId) => {
    return (
      <div className="status-switch">
        <input
          type="checkbox"
          id={`status-${planId}`}
          checked={active}
          onChange={() => handleStatus(planId, active)}
          className="switch-input"
        />
        <label htmlFor={`status-${planId}`} className="switch-label">
          <span className="switch-slider"></span>
        </label>
      </div>
    )
  }

  return (
    <div className="plans-container">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaCreditCard />
            </div>
            <div className="header-text">
              <h1>Gestión de Planes</h1>
              <p>Administra y gestiona los planes de suscripción del sistema</p>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <Card className="actions-card">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={4} md={6} sm={12}>
                <Button 
                  variant="primary" 
                  className="add-plan-btn"
                  onClick={() => navigate('/plan')}
                >
                  <FaPlus />
                  Agregar Plan
                </Button>
              </Col>
              <Col lg={8} md={6} sm={12}>
                <Form onSubmit={handleSearchPlan} className="search-form">
                  <Row>
                    <Col md={12} sm={12} className="mb-2">
                      <div className="search-input-container">
                        <FaSearch className="search-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Buscar planes por nombre o descripción..."
                          value={keywords}
                          onChange={handleChangeKeywords}
                          className="search-input"
                        />
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Plans Table Card */}
        <Card className="table-card">
          <Card.Body>
            {isLoading ? (
              <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3">Cargando planes...</p>
              </div>
            ) : plans.length === 0 ? (
              <div className="empty-state">
                <FaCreditCard className="empty-icon" />
                <h4>No se encontraron planes</h4>
                <p>Intenta con otros términos de búsqueda o agrega un nuevo plan.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-lg-block">
                  <Table className="plans-table">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Nombre</th>
                        <th>Precio</th>
                        <th>Max. invitados</th>
                        <th>Meses</th>
                        <th>Descripción</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan, index) => (
                        <tr key={plan.planId} className="plan-row">
                          <td className="plan-id">#{plan.planId}</td>
                          <td>
                            <Link 
                              to={`/planes/${plan.planId}`}
                              className="plan-name-link"
                            >
                              {plan.planName}
                            </Link>
                          </td>
                          <td className="price-display">
                            {currencyFormat({ number: plan.price })}
                          </td>
                          <td>
                            <div className="plan-features">
                              {plan.maxGuests || '-'}
                            </div>
                          </td>
                          <td>
                            <div className="plan-features">
                              {plan.months}
                            </div>
                          </td>
                          <td className="plan-description" title={plan.description}>
                            {plan.description}
                          </td>
                          <td>
                            <div className="status-toggle">
                              {getStatusSwitch(plan.active, plan.planId)}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/planes/${plan.planId}/actualizar`)}
                                className="edit-btn"
                                title="Editar plan"
                              >
                                <FaPen />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(plan)}
                                className={`delete-btn ${plan.hasMemberships ? 'disabled-btn' : ''}`}
                                title={plan.hasMemberships ? "No se puede eliminar - tiene usuarios asignados" : "Eliminar plan"}
                              >
                                {plan.hasMemberships ? <FaLock /> : <FaTrash />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Tablet Table View - Hide some columns */}
                <div className="table-responsive d-none d-md-block d-lg-none">
                  <Table className="plans-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Precio</th>
                        <th>Max. invitados</th>
                        <th>Meses</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan, index) => (
                        <tr key={plan.planId} className="plan-row">
                          <td>
                            <Link 
                              to={`/planes/${plan.planId}`}
                              className="plan-name-link"
                            >
                              {plan.planName}
                            </Link>
                          </td>
                          <td className="price-display">
                            {currencyFormat({ number: plan.price })}
                          </td>
                          <td>
                            <div className="plan-features">
                              <FaUsers />
                              {plan.maxGuests || '-'}
                            </div>
                          </td>
                          <td>
                            <div className="plan-features">
                              <FaCalendarAlt />
                              {plan.months}
                            </div>
                          </td>
                          <td>
                            <div className="status-toggle">
                              {getStatusSwitch(plan.active, plan.planId)}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/planes/${plan.planId}/actualizar`)}
                                className="edit-btn"
                                title="Editar plan"
                              >
                                <FaPen />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(plan)}
                                className={`delete-btn ${plan.hasMemberships ? 'disabled-btn' : ''}`}
                                title={plan.hasMemberships ? "No se puede eliminar - tiene usuarios asignados" : "Eliminar plan"}
                              >
                                {plan.hasMemberships ? <FaLock /> : <FaTrash />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile Cards View - PrimeReact Style */}
                <div className="plans-cards d-lg-none">
                  {plans.map((plan, index) => (
                    <Card key={plan.planId} className="plan-card">
                      <Card.Body className="plan-card-body">
                        <div className="plan-card-content">
                          <div className="plan-avatar">
                            <FaCreditCard />
                          </div>
                          <div className="plan-info">
                            <h6 className="plan-name">
                              <Link 
                                to={`/planes/${plan.planId}`}
                                className="plan-name-link"
                              >
                                {plan.planName}
                              </Link>
                            </h6>
                            <p className="plan-price">{currencyFormat({ number: plan.price })}</p>
                          </div>
                          <div className="plan-status">
                            <div className="plan-features-column">
                              <div className="plan-feature">
                                <FaUsers />
                                <span>{plan.maxGuests || '-'}</span>
                              </div>
                              <div className="plan-feature">
                                <FaCalendarAlt />
                                <span>{plan.months}</span>
                              </div>
                            </div>
                            <div className="status-column">
                              <div className="status-switch">
                                <input
                                  type="checkbox"
                                  id={`status-${plan.planId}`}
                                  checked={plan.active}
                                  onChange={() => handleStatus(plan.planId, plan.active)}
                                  className="switch-input"
                                />
                                <label htmlFor={`status-${plan.planId}`} className="switch-label">
                                  <span className="switch-slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="plan-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/planes/${plan.planId}/actualizar`)}
                              className="edit-btn"
                              title="Editar plan"
                            >
                              <FaPen />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(plan)}
                              className={`delete-btn ${plan.hasMemberships ? 'disabled-btn' : ''}`}
                              title={plan.hasMemberships ? "No se puede eliminar - tiene usuarios asignados" : "Eliminar plan"}
                            >
                              {plan.hasMemberships ? <FaLock /> : <FaTrash />}
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Modal de Confirmación de Eliminación */}
      <Modal 
        show={showDeleteModal} 
        onHide={cancelDelete}
        centered
        backdrop="static"
        keyboard={false}
        className="delete-confirm-modal"
      >
        <Modal.Header className="delete-modal-header">
          <div className="modal-icon-container">
            <FaExclamationTriangle className="modal-warning-icon" />
          </div>
          <div className="modal-title-container">
            <Modal.Title className="modal-title">Confirmar Eliminación</Modal.Title>
            <p className="modal-subtitle">Esta acción no se puede deshacer</p>
          </div>
          <Button 
            variant="link" 
            onClick={cancelDelete}
            className="modal-close-btn"
            disabled={isDeleting}
          >
            <FaTimes />
          </Button>
        </Modal.Header>
        
        <Modal.Body className="delete-modal-body">
          <div className="plan-preview">
            <h6 className="preview-label">
              {planToDelete?.hasMemberships ? 'Plan con usuarios asignados:' : 'Plan a eliminar:'}
            </h6>
            <div className="plan-content">
              <p className="plan-text">
                <strong>ID:</strong> #{planToDelete?.planId}
              </p>
              <p className="plan-text">
                <strong>Nombre:</strong> {planToDelete?.planName}
              </p>
              <p className="plan-text">
                <strong>Precio:</strong> {planToDelete?.price ? currencyFormat({ number: planToDelete.price }) : 'No especificado'}
              </p>
              <p className="plan-text">
                <strong>Duración:</strong> {planToDelete?.months || 'No especificado'} meses
              </p>
              <p className="plan-text">
                <strong>Máx. Huéspedes:</strong> {planToDelete?.maxGuests || 'No especificado'}
              </p>
            </div>
          </div>
          
          {planToDelete?.hasMemberships ? (
            <div className="info-message">
              <FaLock className="info-icon" />
              <p>Este plan no se puede eliminar porque tiene usuarios asignados. Para poder eliminarlo, primero debes reasignar o eliminar a todos los usuarios que lo tienen activo.</p>
            </div>
          ) : (
            <div className="warning-message">
              <FaExclamationTriangle className="warning-icon" />
              <p>¿Estás seguro de que quieres eliminar este plan? Esta acción es permanente y no se puede deshacer.</p>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer className="delete-modal-footer">
          <Button 
            variant="outline-secondary" 
            onClick={cancelDelete}
            className="cancel-delete-btn"
            disabled={isDeleting}
          >
            {planToDelete?.hasMemberships ? 'Entendido' : 'Cancelar'}
          </Button>
          {!planToDelete?.hasMemberships && (
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              className="confirm-delete-btn"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Eliminando...
                </>
              ) : (
                'Eliminar Plan'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  )
}
